/**
 * Le tableau de bord général : ce qu'il calcule à partir des lectures.
 *
 * Rien ici n'invente un chiffre. Chaque nombre vient d'une lecture de la
 * base (`general.supabase.ts`) ; ce module ne fait que compter, comparer à un
 * seuil et dire où aller.
 *
 * ## Les seuils
 *
 * PROPOSITION TECHNIQUE, À DÉCIDER MÉTIER : « presque complète » à 90 %,
 * « faible » sous 30 % à moins de trois semaines du début, une demande
 * « ancienne » au-delà de 7 jours, un import bancaire « ancien » au-delà de
 * 10 jours. Ils sont tous ici, nommés, pour qu'Éclosion les règle sans
 * chercher.
 */

import { CHEMINS } from '../navigation/chemins';

export const SEUIL_PRESQUE_COMPLETE = 0.9;
export const SEUIL_FAIBLE = 0.3;
export const HORIZON_FAIBLE_JOURS = 21;
export const DEMANDE_ANCIENNE_JOURS = 7;
export const IMPORT_ANCIEN_JOURS = 10;

export interface SessionAVenir {
  id: string;
  debut: string;
  fin: string;
  stage: string | null;
  centre: string | null;
  capacite: number;
  inscrits: number;
}

export interface Remplissage {
  sessions: number;
  capacite: number;
  inscrits: number;
  placesRestantes: number;
  completes: number;
  presqueCompletes: number;
  /** Sous le seuil, et qui commencent dans l'horizon. */
  faibles: number;
}

export interface Demandes {
  annulationsEnAttente: number;
  annulationPlusAncienne: string | null;
  inclusionsEnAttente: number;
  inclusionsSurSessionsAVenir: number;
  listeAttenteSurSessionsAVenir: number;
}

export interface DernierImport {
  creeLe: string;
  dateReleve: string | null;
  periodeFin: string | null;
}

export interface Alerte {
  niveau: 'alerte' | 'attention';
  message: string;
  lien: string;
}

/** Aujourd'hui, en date belge, au format `AAAA-MM-JJ`. */
export function aujourdhuiABruxelles(maintenant = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels' }).format(maintenant);
}

/** Jours entiers entre deux dates `AAAA-MM-JJ` (ou horodatages), b − a. */
export function joursEntre(a: string, b: string): number {
  const j = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
  return Math.round((j(b) - j(a)) / 86_400_000);
}

/** `null` quand la capacité est nulle : un taux sur zéro place ne dit rien. */
export function taux(s: Pick<SessionAVenir, 'capacite' | 'inscrits'>): number | null {
  return s.capacite > 0 ? s.inscrits / s.capacite : null;
}

export function estFaible(s: SessionAVenir, aujourdhui: string): boolean {
  const t = taux(s);
  return t !== null && t < SEUIL_FAIBLE && joursEntre(aujourdhui, s.debut) <= HORIZON_FAIBLE_JOURS;
}

export function remplissage(sessions: readonly SessionAVenir[], aujourdhui: string): Remplissage {
  let capacite = 0;
  let inscrits = 0;
  let placesRestantes = 0;
  let completes = 0;
  let presqueCompletes = 0;
  let faibles = 0;
  for (const s of sessions) {
    capacite += Math.max(0, s.capacite);
    inscrits += Math.max(0, s.inscrits);
    placesRestantes += Math.max(0, s.capacite - s.inscrits);
    const t = taux(s);
    if (t === null) continue;
    if (t >= 1) completes += 1;
    else if (t >= SEUIL_PRESQUE_COMPLETE) presqueCompletes += 1;
    if (estFaible(s, aujourdhui)) faibles += 1;
  }
  return { sessions: sessions.length, capacite, inscrits, placesRestantes, completes, presqueCompletes, faibles };
}

const pluriel = (n: number, un: string, plusieurs: string) => `${n} ${n > 1 ? plusieurs : un}`;

/**
 * Les alertes, chacune vers l'espace qui sait la traiter. Une lecture qui a
 * échoué arrive ici en `null` et ne produit aucune alerte : l'écran dit
 * ailleurs que le bloc est illisible, il ne fabrique pas un « tout va bien ».
 *
 * Seule exception, `dernierImport` : `undefined` pour une lecture échouée,
 * `null` pour une lecture réussie qui n'a trouvé aucun import.
 */
export function alertes(
  d: {
    sessions: readonly SessionAVenir[] | null;
    demandes: Demandes | null;
    dernierImport: DernierImport | null | undefined;
    mouvementsATraiter: number | null;
    piecesAComptabiliser: number | null;
  },
  aujourdhui: string,
): Alerte[] {
  const out: Alerte[] = [];

  if (d.sessions) {
    const pleines = d.sessions.filter((s) => (taux(s) ?? 0) >= SEUIL_PRESQUE_COMPLETE);
    if (pleines.length > 0) {
      out.push({
        niveau: 'attention',
        message: `${pluriel(pleines.length, 'session à venir', 'sessions à venir')} à ${Math.round(SEUIL_PRESQUE_COMPLETE * 100)} % ou plus de sa capacité`,
        lien: CHEMINS.planification.programmation,
      });
    }
    const faibles = d.sessions.filter((s) => estFaible(s, aujourdhui));
    if (faibles.length > 0) {
      out.push({
        niveau: 'attention',
        message: `${pluriel(faibles.length, 'session', 'sessions')} sous ${Math.round(SEUIL_FAIBLE * 100)} % de remplissage, à moins de ${HORIZON_FAIBLE_JOURS} jours du début`,
        lien: CHEMINS.planification.programmation,
      });
    }
  }

  if (d.demandes) {
    const a = d.demandes;
    if (a.annulationsEnAttente > 0 && a.annulationPlusAncienne
        && joursEntre(a.annulationPlusAncienne, aujourdhui) > DEMANDE_ANCIENNE_JOURS) {
      out.push({
        niveau: 'alerte',
        message: `Une demande d’annulation attend depuis ${joursEntre(a.annulationPlusAncienne, aujourdhui)} jours`,
        lien: CHEMINS.planification.annulations,
      });
    }
    if (a.inclusionsSurSessionsAVenir > 0) {
      out.push({
        niveau: 'attention',
        message: `${pluriel(a.inclusionsSurSessionsAVenir, 'demande d’inclusion en attente', 'demandes d’inclusion en attente')} sur des sessions à venir`,
        lien: CHEMINS.planification.inclusion,
      });
    }
  }

  if (d.dernierImport) {
    const age = joursEntre(d.dernierImport.creeLe, aujourdhui);
    if (age > IMPORT_ANCIEN_JOURS) {
      out.push({
        niveau: 'attention',
        message: `Dernier import bancaire il y a ${age} jours`,
        lien: CHEMINS.comptabilite.rapprochement,
      });
    }
  } else if (d.dernierImport === null) {
    out.push({ niveau: 'attention', message: 'Aucun import bancaire', lien: CHEMINS.comptabilite.rapprochement });
  }

  if (d.mouvementsATraiter !== null && d.mouvementsATraiter > 0) {
    out.push({
      niveau: 'attention',
      message: `${pluriel(d.mouvementsATraiter, 'mouvement bancaire à imputer', 'mouvements bancaires à imputer')}`,
      lien: CHEMINS.comptabilite.rapprochement,
    });
  }

  if (d.piecesAComptabiliser !== null && d.piecesAComptabiliser > 0) {
    out.push({
      niveau: 'attention',
      message: `${pluriel(d.piecesAComptabiliser, 'pièce de vente attend', 'pièces de vente attendent')} le journal des ventes`,
      lien: CHEMINS.comptabilite.accueil,
    });
  }

  return out;
}
